import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Neslihan Gündüz Vatansever — a personal contact page.
 *
 * Asked for 2026-09-15 with the address written out in full
 * (`/en/about/team/neslihan-gunduz-vatansever`), so that is the slug: the link
 * is presumably already being handed out. Nothing existed under `about/team/`
 * before this — it is the first page there, and deliberately just one page
 * rather than a team index nobody asked for.
 *
 * The three facts supplied are the whole of the content: name, mobile, email.
 * No role, no portrait and no biography were given, and inventing a job title
 * for a named colleague is the one thing a page like this must not do — so the
 * page says what it knows and carries a `check` asking for the rest.
 *
 * Asked for 2026-09-18: the page should read "Neslihan Gündüz". Only the
 * displayed name changed — the slug, the vCard filename and the QR link behind
 * them stay as they are, because that address is already being handed out.
 *
 * Asked for 2026-09-24: the address is `/en/about/team/neslihan-gunduz`, which
 * also matches the other team cards (first name + surname, no second surname).
 * The page is reached by scanning a QR code and by nothing else — it is in no
 * menu (nothing reads `navLabel` to build the navigation) and `robots: 'noindex'`
 * keeps it out of the sitemap and out of search. **The long
 * `/neslihan-gunduz-vatansever` no longer resolves**: the redirect table is
 * generated from the legacy site and cannot be added to from here, so any code
 * already printed with the long link needs reprinting with this one. The vCard
 * keeps its filename: `verify-vcards.mjs` pairs it with this page's *file* name,
 * which has not changed.
 *
 * `tel:` is the number with no spaces and the country code kept, which is what
 * a phone dials; the spaced form is what the reader sees.
 *
 * Asked for 2026-09-15: the team contact pages should read as one set, so this
 * page follows the shared shape — a centred name, a `features` grid whose card
 * title *is* the address or the number and is itself the link, and the closing
 * band back to the firm that every other card ends on (which replaced the
 * paragraph that used to say the same thing in prose). The eyebrow stays
 * "Digitopia" rather than a role, because no role was given; the `check` below
 * is still the one asking for it.
 */
export const route: RouteDef = {
  id: 'teamNeslihanGunduzVatansever',
  path: 'about/team/neslihan-gunduz',
  // Asked for 2026-09-15: the contact cards are reached by scanning a QR code
  // and by nothing else, so this page stays out of the sitemap and out of
  // search results. The link itself is unaffected.
  robots: 'noindex',
  priority: 0.5,
}

const EMAIL = 'neslihan@digitopia.ai'
const PHONE_DISPLAY = '+90 536 724 81 75'
const PHONE_DIAL = 'tel:+905367248175'
// Asked for 2026-09-17, for every team page: a button that saves the details
// straight into the reader's phone. The file is a static vCard in
// `public/vcf/`, named after this page's slug — see that folder's README.
const VCARD = '/vcf/neslihan-gunduz-vatansever.vcf'

export const teamNeslihanGunduzVatansever: WrittenPage = {
  navLabel: 'Neslihan Gündüz',
  title: 'Neslihan Gündüz',
  description:
    'Contact Neslihan Gündüz at Digitopia: neslihan@digitopia.ai, +90 536 724 81 75.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Digitopia',
      headline: 'Neslihan Gündüz',
      align: 'center',
      ctas: [
        { label: 'Email', href: `mailto:${EMAIL}` },
        { label: 'Call', href: PHONE_DIAL },
        { label: 'Save contact', href: VCARD },
      ],
      check:
        'No role, title or portrait was supplied for Neslihan, so the page carries ' +
        'her name and her contact details and nothing else. Send the job title (and ' +
        'a portrait, and a LinkedIn profile if it should be linked) and this becomes ' +
        'a proper profile. Also confirm that a personal mobile number is meant to be ' +
        'public on digitopia.ai — the rest of the site publishes no direct numbers.',
    },
    {
      kind: 'features',
      heading: 'Get in touch',
      columns: 2,
      items: [
        {
          title: EMAIL,
          note: 'Email',
          href: `mailto:${EMAIL}`,
        },
        {
          title: PHONE_DISPLAY,
          note: 'Türkiye',
          href: PHONE_DIAL,
        },
      ],
    },
    {
      kind: 'cta',
      heading: 'Rather start with the firm than a person?',
      sub: 'About Us says who else you would be working with.',
      tone: 'band',
      ctas: [
        { label: 'Contact Digitopia', href: LINKS.contact },
        { label: 'About Digitopia', href: LINKS.about },
      ],
    },
  ],
}
